import {
  BuildingEnergyModelFragment,
  BuildingEnergyModelFragmentDoc,
  CreateBuildingEnergyModelInput,
  ProjectFragment,
  UpdateBuildingEnergyModelInput,
  useCreateBuildingEnergyModelMutation,
  useDeleteBuildingEnergyModelMutation,
  useUpdateBuildingEnergyModelMutation,
} from '../generated/graphql'
import { useCacheHelpers } from '../apollo/cache-helpers'

type BuildingEnergyModelMutations = {
  isCreateLoading: boolean
  isUpdateLoading: boolean
  isDeleteLoading: boolean

  createBuildingEnergyModel: (
    project: ProjectFragment,
    input: CreateBuildingEnergyModelInput,
  ) => Promise<BuildingEnergyModelFragment>

  updateBuildingEnergyModel: (
    id: string,
    input: UpdateBuildingEnergyModelInput,
  ) => Promise<BuildingEnergyModelFragment>

  deleteBuildingEnergyModel: (
    project: ProjectFragment,
    id: string,
  ) => Promise<boolean>
}

export function useBuildingEnergyModels(): BuildingEnergyModelMutations {
  const [createBuildingEnergyModel, { loading: isCreateLoading }] =
    useCreateBuildingEnergyModelMutation()
  const [updateBuildingEnergyModel, { loading: isUpdateLoading }] =
    useUpdateBuildingEnergyModelMutation()
  const [deleteBuildingEnergyModel, { loading: isDeleteLoading }] =
    useDeleteBuildingEnergyModelMutation()
  const { addItemToList, removeItemFromList } = useCacheHelpers()

  return {
    isCreateLoading,
    isUpdateLoading,
    isDeleteLoading,

    async createBuildingEnergyModel(
      project: ProjectFragment,
      input: CreateBuildingEnergyModelInput,
    ) {
      const result = await createBuildingEnergyModel({
        variables: {
          projectId: project.id,
          input,
        },

        update: (_, mutationResult) => {
          const building = mutationResult.data?.createBuildingEnergyModel
          if (!building) {
            return
          }

          addItemToList(
            BuildingEnergyModelFragmentDoc,
            building,
            project,
            'buildingEnergyModels',
          )
        },
      })

      if (!result.data) {
        throw new Error('Building energy model creation failed')
      }

      return result.data.createBuildingEnergyModel
    },

    async updateBuildingEnergyModel(
      id: string,
      input: UpdateBuildingEnergyModelInput,
    ) {
      const result = await updateBuildingEnergyModel({
        variables: { id, input },
      })

      if (!result.data) {
        throw new Error('Building energy model update failed')
      }

      return result.data.updateBuildingEnergyModel
    },

    async deleteBuildingEnergyModel(project: ProjectFragment, id: string) {
      const result = await deleteBuildingEnergyModel({
        variables: { id },

        update: () => {
          removeItemFromList(
            BuildingEnergyModelFragmentDoc,
            id,
            project,
            'buildingEnergyModels',
          )
        },

        optimisticResponse: {
          deleteBuildingEnergyModel: true,
        },
      })

      if (!result.data) {
        throw new Error('Building energy model deletion failed')
      }

      return result.data.deleteBuildingEnergyModel
    },
  }
}
